import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const H1_LINES = ["Are your ads finding", "paying patients.", "Or price-checkers?"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.15 + i * 0.12,
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.55 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // subtle parallax lift on the stat card as the user scrolls away
  const statY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.55]);

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "var(--rtd-bg)" }}
      data-testid="hero-section"
    >
      {/* faint gold aura, purely tonal — no imagery */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 720,
          height: 720,
          background:
            "radial-gradient(circle, rgba(232,160,32,0.10) 0%, rgba(232,160,32,0) 62%)",
          pointerEvents: "none",
        }}
      />

      <motion.div
        style={{ opacity: heroOpacity }}
        className="relative mx-auto text-center"
      >
        <div
          className="mx-auto"
          style={{
            maxWidth: 680,
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 96,
            paddingBottom: 96,
          }}
        >
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="eyebrow"
            style={{ marginBottom: 24 }}
            data-testid="hero-eyebrow"
          >
            Patient Quality Assessment
          </motion.p>

          <h1
            className="font-serif-display"
            style={{ color: "var(--rtd-text)", lineHeight: 1.1, marginBottom: 24 }}
            data-testid="hero-headline"
          >
            {H1_LINES.map((line, i) => (
              <span className="line-mask" key={line}>
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ display: "block" }}
                  className="text-[36px] sm:text-[52px]"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-body mx-auto"
            style={{
              fontSize: 18,
              color: "var(--rtd-text-secondary)",
              maxWidth: 540,
              lineHeight: 1.7,
            }}
            data-testid="hero-subheadline"
          >
            7 questions. 3 minutes. Find out exactly how well your Meta setup is trained
            to find paying patients. And where it is breaking.
          </motion.p>

          {/* Stat callout card */}
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            style={{ y: statY }}
            className="mx-auto"
          >
            <div
              className="mx-auto flex items-center gap-5 text-left"
              style={{
                marginTop: 40,
                maxWidth: 560,
                backgroundColor: "var(--rtd-card)",
                border: "1px solid var(--rtd-border)",
                borderRadius: 12,
                padding: "20px 24px",
                boxShadow: "0 1px 2px rgba(17,24,39,0.04)",
              }}
              data-testid="hero-stat-card"
            >
              <span
                className="font-serif-display shrink-0"
                style={{ fontSize: 44, color: "var(--rtd-gold)", lineHeight: 1 }}
              >
                25–35%
              </span>
              <span className="font-body">
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: "var(--rtd-text-secondary)",
                    lineHeight: 1.5,
                  }}
                >
                  Average no-show rate for new patient consultations at clinics running
                  price-driven Meta ads. For revenue-trained clinics, this drops to 8–12%.
                </span>
                <span
                  style={{ display: "block", marginTop: 6, fontSize: 11, color: "var(--rtd-muted)" }}
                >
                  ProspyrMed, 2025
                </span>
              </span>
            </div>
          </motion.div>

          <motion.p
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-body"
            style={{
              marginTop: 24,
              fontSize: 13,
              fontStyle: "italic",
              color: "var(--rtd-muted)",
            }}
            data-testid="hero-privacy-note"
          >
            Your results are calculated locally. No email required to see your score.
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
}
